import React, { type ComponentProps, useCallback } from 'react';
import { Tabs, useRouter, useSegments } from 'expo-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { BodyText, Icon, Navbar, NavbarBackLink, theme } from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';

import { useBagCount } from '../../../../hooks';
import { useNavigateBack } from '../../../../navigation';
import { NAVBAR_BAG_ICON_SIZE, TABS } from '../../NavbarV2.constant';
import { navbarV2Messages as messages } from '../../NavbarV2.messages';
import { NavbarLink } from '../NavbarLink';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarXS = (props: Props) => {
  const { tabBarProps } = props;

  const router = useRouter();
  const segments = useSegments();
  const navigateBack = useNavigateBack();

  const { formatMessage } = useIntl();
  const { track } = useTelemetry();
  const { bagCount } = useBagCount();

  // ─── Derived ────────────────────────────────────────────────────────

  const activeRoute = tabBarProps.state.routes[tabBarProps.state.index];

  const isRewardsTabActive = activeRoute?.name === TABS.REWARDS;
  const isGiftTabActive = activeRoute?.name === TABS.GIFT;
  const isMenuTabActive = activeRoute?.name === TABS.MENU;

  const shouldRenderCompactNavbar = (segments.length >= 3 && !isMenuTabActive) || isGiftTabActive || isRewardsTabActive; // prettier-ignore
  const hasActiveBag = bagCount > 0;

  const routes = shouldRenderCompactNavbar
    ? tabBarProps.state.routes.filter(checkIfHomeRoute)
    : tabBarProps.state.routes;

  // ─── Handlers ───────────────────────────────────────────────────────

  const handleBackPress = useCallback(() => {
    if (isGiftTabActive || isRewardsTabActive) {
      router.replace('/account');
      return;
    }

    navigateBack();
  }, [isGiftTabActive, isRewardsTabActive, navigateBack, router]);

  const handleHomePress = useCallback(() => {
    track('home.jump_nav');
    navigateToTab({ ...tabBarProps, tabName: TABS.HOME });
  }, [tabBarProps, track]);

  const handleMenuPress = useCallback(() => {
    track('menu.jump_nav', {});
    navigateToTab({ ...tabBarProps, tabName: TABS.MENU });
  }, [tabBarProps, track]);

  const handleAccountPress = useCallback(() => {
    track('account.jump_nav');
    navigateToTab({ ...tabBarProps, tabName: TABS.ACCOUNT });
  }, [tabBarProps, track]);

  const handleStartOrderPress = useCallback(() => {
    track('order.jump_nav');
    navigateToTab({ ...tabBarProps, tabName: TABS.MENU });
  }, [tabBarProps, track]);

  const handleBagPress = useCallback(() => {
    track('bag.jump_nav');
    router.navigate('/bag');
  }, [router, track]);

  const renderBagIcon = useCallback(() => {
    return <NavbarBagIcon bagCount={bagCount} />;
  }, [bagCount]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Navbar.Container>
      <Navbar.LinksGroup>
        {shouldRenderCompactNavbar ? (
          <NavbarBackLink
            accessibilityLabel={formatMessage(messages.goBack)}
            onPress={handleBackPress}
          />
        ) : undefined}

        {routes.map((route, index) => (
          <NavbarLink
            key={route.key}
            route={route}
            index={index}
            activeIndex={tabBarProps.state.index}
            onAccountPress={handleAccountPress}
            onHomePress={handleHomePress}
            onMenuPress={handleMenuPress}
          />
        ))}
      </Navbar.LinksGroup>

      {hasActiveBag ? (
        <Navbar.Button rightElement={renderBagIcon} onPress={handleBagPress}>
          <FormattedMessage {...messages.bag} />
        </Navbar.Button>
      ) : (
        <Navbar.Button onPress={handleStartOrderPress}>
          <FormattedMessage {...messages.startOrder} />
        </Navbar.Button>
      )}
    </Navbar.Container>
  );
};

// ─── Components ──────────────────────────────────────────────────────────────

const NavbarBagIcon = (props: NavbarBagIconProps) => {
  const { bagCount } = props;

  return (
    <View style={styles.bagIconContainer}>
      <Icon
        name="IconBagFill"
        width={NAVBAR_BAG_ICON_SIZE}
        height={NAVBAR_BAG_ICON_SIZE}
        color={theme.colors.WHITE}
      />

      <BodyText sizeMatch={['12']} style={styles.bagIconLabel}>
        {bagCount > 9 ? '9+' : bagCount}
      </BodyText>
    </View>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function navigateToTab(params: NavigateToTabParams) {
  const { descriptors, navigation, state, tabName } = params;

  const route = state.routes.find((stateRoute) => stateRoute.name === tabName);

  if (!route) {
    return;
  }

  const event = navigation.emit({ type: 'tabPress', target: route.key, canPreventDefault: true });

  if (event.defaultPrevented) {
    return;
  }

  const options = descriptors[route.key].options as TabOptions;

  options.onPress?.();
  navigation.navigate(tabName, undefined, { merge: true });
}

function checkIfHomeRoute(route: TabRoute) {
  return route.name === TABS.HOME;
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  bagIconContainer: {
    marginLeft: theme.spacing['1'],
    marginTop: -2, // to better align with label
  },
  bagIconLabel: {
    position: 'absolute',
    right: 0,
    bottom: 1,
    left: 0,
    textAlign: 'center',
    color: theme.colors.KALE,
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type Props = {
  tabBarProps: TabBarProps;
};

type TabBarProps = Parameters<NonNullable<ComponentProps<typeof Tabs>['tabBar']>>[0];
type TabRoute = TabBarProps['state']['routes'][number];
type TabOptions = { onPress?: () => void };

type NavigateToTabParams = TabBarProps & { tabName: NavbarTabName };
type NavbarTabName = typeof TABS.HOME | typeof TABS.MENU | typeof TABS.ACCOUNT;
type NavbarBagIconProps = { bagCount: number };
